import { useState } from 'react';
import { Loader2, LogIn } from 'lucide-react';
import { signInWithMicrosoft, type AuthUser } from '../api/auth';

interface Props {
  brand: string;
  onSignedIn: (user: AuthUser) => void;
}

/**
 * Single-button Microsoft 365 SSO sign-in screen. Clicking the button opens
 * an Office dialog that runs the full Cognito → Entra ID OIDC flow; on
 * success the resolved AuthUser is handed back to App.tsx via onSignedIn.
 *
 * The add-in is SSO-only — there is intentionally no email/password fallback
 * (see docs/2026-06-10-sso-federation-final.md §3.5).
 */
export function LoginScreen({ brand, onSignedIn }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const user = await signInWithMicrosoft();
      onSignedIn(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full flex-col justify-center px-5 py-6">
      <div className="mb-6 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-500 text-white shadow-soft">
          <LogIn className="h-6 w-6" />
        </div>
        <h1 className="text-lg font-semibold text-ink-900">{brand}</h1>
        <p className="text-sm text-ink-500">
          Sign in with your Microsoft 365 account to use your AI agents from Office.
        </p>
      </div>

      <div className="space-y-3">
        <button
          type="button"
          onClick={start}
          disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MicrosoftLogo className="h-4 w-4" />
          )}
          {busy ? 'Opening Microsoft sign-in…' : 'Sign in with Microsoft'}
        </button>

        {error && (
          <div role="alert" className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}
      </div>

      <p className="mt-6 text-center text-[11px] leading-relaxed text-ink-300">
        Uses your Microsoft 365 account via single sign-on. The add-in stores no
        passwords — Cognito ID tokens live in browser storage for this Office
        session only.
      </p>
    </div>
  );
}

/**
 * 4-square Microsoft brand mark, sized to match the lucide-react icons used
 * elsewhere in the add-in. Kept inline so we don't pull in an icon font for
 * a single button.
 */
function MicrosoftLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 23 23"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1"  y="1"  width="10" height="10" fill="#F25022" />
      <rect x="12" y="1"  width="10" height="10" fill="#7FBA00" />
      <rect x="1"  y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}
